import React, { useState } from 'react';
import { Box, Container, Typography, Grid, Paper, Chip, Stack, IconButton, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ContentCopyIcon  from '@mui/icons-material/ContentCopy';
import CheckCircleIcon  from '@mui/icons-material/CheckCircle';
import { SwapWidget } from '@uniswap/widgets';
import { useWeb3React } from '@web3-react/core';
import './SwapWidget.css';

import ArbitrumLogo from '../../resources/arbitrum.svg';

const EBONDS_ADDRESS = '0x53Ee546eB38fB2C8b870f56DeeaeCF80367a4551';
const ESIR_ADDRESS   = '0x8C75a1C86C21b74754FC8e3Bc4e7f79B4fCC5a28';
const USDC_ADDRESS   = '0xaf88d065e77c8cC2239327C5EDb3A432268e5831';
const JSON_RPC_URL   = 'https://arb1.arbitrum.io/rpc';

const MY_TOKEN_LIST = [
  { name: 'Ebonds',   address: EBONDS_ADDRESS, symbol: 'EBONDS', decimals: 18, chainId: 42161, logoURI: 'https://arbiscan.io/token/images/ebondsfinance_32.png' },
  { name: 'Esir',     address: ESIR_ADDRESS,   symbol: 'ESIR',   decimals: 18, chainId: 42161, logoURI: 'https://arbiscan.io/token/images/ebondsesir_64.png' },
  { name: 'USD Coin', address: USDC_ADDRESS,   symbol: 'USDC',   decimals: 6,  chainId: 42161, logoURI: 'https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/ethereum/assets/0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48/logo.png' },
];

const darkSwapTheme = {
  primary:     '#FFFFFF',
  secondary:   '#94a3b8',
  interactive: 'transparent',
  container:   '#0a1019',
  module:      '#05090f',
  accent:      '#d29d5c',
  outline:     '#293245',
  dialog:      '#0a1019',
  fontFamily:  '"Space Grotesk", sans-serif',
  borderRadius: 0.5,
};

const lightSwapTheme = {
  primary:     '#0a1020',
  secondary:   '#5c6b7f',
  interactive: 'transparent',
  container:   '#ffffff',
  module:      '#f7f9fc',
  accent:      '#c8904a',
  outline:     '#e5eaf0',
  dialog:      '#ffffff',
  fontFamily:  '"Space Grotesk", sans-serif',
  borderRadius: 0.5,
};

/* ─── Address row ─────────────────────────────────────────── */
const AddressRow = ({ label, address }) => {
    const theme  = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(address);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                {label}
            </Typography>
            <Stack
                direction="row" alignItems="center" justifyContent="space-between"
                sx={{
                    bgcolor: isDark ? 'rgba(0,0,0,0.3)' : 'rgba(10,16,32,0.04)',
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : theme.palette.divider}`,
                    p: 1, borderRadius: 1,
                }}
            >
                <Typography variant="caption" color="primary.main" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
                    {address}
                </Typography>
                <Tooltip title={copied ? 'Copied!' : 'Copy Address'}>
                    <IconButton size="small" onClick={handleCopy} sx={{ color: copied ? 'success.main' : 'text.secondary', flexShrink: 0 }}>
                        {copied ? <CheckCircleIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                    </IconButton>
                </Tooltip>
            </Stack>
        </Box>
    );
};

/* ─── Page ───────────────────────────────────────────────── */
const AboutPage = () => {
    const { library } = useWeb3React();
    const theme  = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const swapTheme  = isDark ? darkSwapTheme : lightSwapTheme;
    const paperBg    = isDark ? 'rgba(255,255,255,0.02)' : 'background.paper';
    const paperBorder = isDark ? '1px solid rgba(255,255,255,0.06)' : `1px solid ${theme.palette.divider}`;
    const widgetBorder = isDark ? '1px solid rgba(255,255,255,0.1)' : `1px solid ${theme.palette.divider}`;
    const widgetShadow = isDark ? '0 20px 50px rgba(0,0,0,0.5)' : '0 8px 32px rgba(10,16,32,0.1)';

    return (
        <Box sx={{
            minHeight: '100vh',
            pt: { xs: 12, md: 16 },
            pb: 8,
            bgcolor: 'background.default',
            backgroundImage: isDark
                ? 'radial-gradient(circle at 50% 50%, rgba(210,157,92,0.04), transparent 50%)'
                : 'none',
        }}>
            <Container maxWidth="lg">
                <Grid container spacing={8} justifyContent="center" alignItems="flex-start">

                    {/* LEFT: context & contracts */}
                    <Grid item xs={12} md={6}>
                        <Box sx={{ mb: 4 }}>
                            <Chip
                                icon={<img src={ArbitrumLogo} alt="Arb" style={{ width: 16, height: 16 }} />}
                                label="Arbitrum One"
                                sx={{
                                    mb: 2, fontWeight: 700,
                                    bgcolor: isDark ? 'rgba(45,55,75,0.5)' : 'rgba(42,90,218,0.08)',
                                    color:   isDark ? '#96bedc'            : '#2A5ADA',
                                    border:  isDark ? 'none' : '1px solid rgba(42,90,218,0.2)',
                                }}
                            />
                            <Typography variant="h2" fontWeight={700} color="text.primary" gutterBottom>
                                Instant <span style={{ color: theme.palette.primary.main }}>Swap</span>
                            </Typography>
                            <Typography variant="h6" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                                Exchange assets securely with zero platform fees.
                                Liquidity is routed through the most efficient pools on Arbitrum.
                            </Typography>
                        </Box>

                        <Paper sx={{ p: 3, bgcolor: paperBg, border: paperBorder }}>
                            <Typography variant="subtitle2" fontWeight={700} color="text.primary" gutterBottom sx={{ mb: 3, letterSpacing: '0.1em' }}>
                                OFFICIAL CONTRACTS
                            </Typography>
                            <AddressRow label="EBONDS Token"      address={EBONDS_ADDRESS} />
                            <AddressRow label="ESIR Token"        address={ESIR_ADDRESS}   />
                            <AddressRow label="USDC (Arbitrum)" address={USDC_ADDRESS}   />
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', fontStyle: 'italic' }}>
                                *Always verify the contract address before trading.
                            </Typography>
                        </Paper>
                    </Grid>

                    {/* RIGHT: swap widget */}
                    <Grid item xs={12} md={5}>
                        <Box
                            data-theme={isDark ? 'dark' : 'light'}
                            sx={{
                                boxShadow: widgetShadow,
                                border: widgetBorder,
                                borderRadius: 2,
                                overflow: 'hidden',
                            }}
                        >
                            <div className="Uniswap">
                                <SwapWidget
                                    provider={library}
                                    jsonRpcUrlMap={{ 42161: JSON_RPC_URL }}
                                    defaultChainId={42161}
                                    theme={swapTheme}
                                    width="100%"
                                    tokenList={MY_TOKEN_LIST}
                                    defaultInputTokenAddress={USDC_ADDRESS}
                                    defaultOutputTokenAddress={EBONDS_ADDRESS}
                                />
                            </div>
                        </Box>
                    </Grid>

                </Grid>
            </Container>
        </Box>
    );
};

export default AboutPage;
